import React, { useEffect } from "react";

// Material Components
import { Icon, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { useTheme } from "@emotion/react";

// Common Components
import MDTypography from "components/MDTypography";
import FilterButton from "components/Buttons/FilterButton";
import pxToRem from "assets/theme/functions/pxToRem";
import MDBox from "components/MDBox";

// Utils
import Constants, { Icons, Colors, ButtonTitles } from "utils/Constants";
import { timeAgo, identifyNotificationRoute } from "utils/methods/methods";

// 3rd Party
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { getPoperNotifications, markAllAsRead } from "redux/Thunks/Notification";
import { markAllNotificationasRead } from "redux/Slice/SystemNotification";

export default function NotificationDialog({ open, onClose }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { poperNotifications = [], poperNotificationLoading } = useSelector(
    (state) => state.systemNotification
  );
  const unreadNotifications = poperNotifications?.filter((n) => !n.isRead);
  const topThreeUnread = unreadNotifications.slice(0, 5);

  const handleCloseDialog = () => {
    onClose();
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (open && event.target.closest(".notification-dialog") === null) {
        handleCloseDialog(); // Close the dialog
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      dispatch(getPoperNotifications());
    }
  }, [open]);

  const handleMarkAllasRead = async () => {
    const res = await dispatch(markAllAsRead());
    if (res?.payload?.status === 200) {
      dispatch(markAllNotificationasRead());
    }
  };
  return (
    open && (
      <MDBox
        className="notification-dialog"
        onClose={onClose}
        sx={{
          position: "absolute",
          right: 0,
          top: "100%",
          width: pxToRem(432),
          backgroundColor: Colors.WHITE,
          padding: pxToRem(16),
          boxShadow: "4px 14px 34px 0px rgba(0, 0, 0, 0.15)",
          [theme.breakpoints.up("md")]: {
            width: pxToRem(432),
            top: "100%",
          },
          [theme.breakpoints.only("xs")]: {
            width: "100%",
            top: "100%",
          },
        }}
      >
        <MDBox>
          <MDTypography
            sx={{
              color: Colors.BLACK2,
              fontSize: pxToRem(20),
              fontWeight: "600",
              borderBottom: "1px solid #E0E2E7",
            }}
          >
            Notification
          </MDTypography>
        </MDBox>
        <List>
          {topThreeUnread?.length > 0 && poperNotificationLoading === Constants.FULFILLED ? (
            topThreeUnread?.slice(0, 5).map((data) => (
              <Link
                to={identifyNotificationRoute(
                  data?.notification?.module,
                  data?.notification?.actionUrl
                )}
              >
                <ListItem disablePadding key={data.id}>
                  <ListItemButton>
                    <ListItemText>
                      <MDBox display="flex" flexDirection="column">
                        <MDBox display="flex" flexDirection={{ xs: "column", md: "row" }}>
                          <MDBox display="flex" flexDirection="row">
                            <Icon>{Icons.PERSON}</Icon>
                            <MDTypography
                              sx={{
                                color: Colors.BLACK2,
                                fontSize: pxToRem(14),
                                fontWeight: "400",
                                marginLeft: pxToRem(8),
                              }}
                            >
                              From{" "}
                              {`${data?.notification?.sender?.firstName} ${data?.notification?.sender?.lastName}`}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" flexDirection="row" mt={{ xs: "5px", md: "0" }}>
                            <Icon sx={{ marginLeft: { xs: pxToRem(0), md: pxToRem(8) } }}>
                              {Icons.TIME}
                            </Icon>
                            <MDTypography
                              sx={{
                                color: Colors.BLACK2,
                                fontSize: pxToRem(14),
                                fontWeight: "400",
                                marginLeft: pxToRem(8),
                              }}
                            >
                              {timeAgo(data?.createdAt)}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDTypography
                          sx={{
                            color: Colors.BLACK2,
                            fontSize: pxToRem(16),
                            fontWeight: "700",
                            marginTop: pxToRem(5),
                          }}
                        >
                          {data?.notification?.title}
                        </MDTypography>
                        <MDBox>
                          <MDTypography
                            sx={{
                              color: Colors.BLACK2,
                              fontSize: pxToRem(14),
                              fontWeight: "400",
                              marginTop: pxToRem(4),
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: "2",
                              WebkitBoxOrient: "vertical",
                              lineClamp: "2",
                            }}
                          >
                            {data?.notification?.body}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
              </Link>
            ))
          ) : (
            <MDBox padding={pxToRem(16)} display="flex" justifyContent="center" alignItems="center">
              <MDTypography
                sx={{
                  color: Colors.BLACK2,
                  fontSize: pxToRem(16),
                  fontWeight: "600",
                }}
              >
                No new notification
              </MDTypography>
            </MDBox>
          )}
        </List>
        <MDBox
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          sx={{ borderTop: "1px solid #E0E2E7" }}
        >
          {topThreeUnread?.length > 0 && (
            <FilterButton
              title={ButtonTitles.MARK_ALL_AS_READ}
              variant="none"
              color={Colors.PRIMARY}
              icon={Icons.ACCEPT3}
              mt={pxToRem(4)}
              handleClick={handleMarkAllasRead}
            />
          )}
          {topThreeUnread?.length > 0 && (
            <MDBox display="flex" flexDirection="row" marginTop="8px">
              <MDTypography
                component={Link}
                to="/client/setting/system-notification"
                sx={{
                  color: Colors.PRIMARY,
                  fontSize: pxToRem(14),
                  fontWeight: "600",
                  marginRight: pxToRem(8),
                }}
              >
                See More
              </MDTypography>
              <Icon>{Icons.FORWARD_ARROW}</Icon>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    )
  );
}

NotificationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
